<template>
  <o-dialog
    ref="dialog"
    :title="$t('general.edit')"
    :info="$t('order.editDetails')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.saveChanges'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div v-if="stagingOrder" class="pt-4 text-left -mb-8">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('general.customer')" class="w-full mb-4 block" rules="required">
          <customer-select v-model="stagingOrder.customer" class="w-full" :status="status" />
        </o-field-group>

        <o-field-group v-slot="{ status }" :label="$t('general.employee')" class="w-full mb-4 block">
          <employee-select v-model="stagingOrder.employee" :types="['employee']" class="w-full" :status="status" allow-none />
        </o-field-group>

        <o-field-group :label="$t('general.date')" class="w-full mb-4 block" rules="required">
          <date-field v-model="stagingOrder.date" :augmenter="Augmenters.timestamp" :timezone="timezone" />
        </o-field-group>

        <o-custom-fields :data="stagingOrder.customFields">
          <template #default="{ fields }">
            <o-field-group
              v-for="field in fields"
              :key="field.props.id"
              :label="field.label"
              class="w-full mb-4 block"
            >
              <component
                :is="field.component"
                v-bind="field.props"
                v-on="field.events"
              />
            </o-field-group>
          </template>
        </o-custom-fields>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import CustomerSelect from '@/components/customer/elements/Select.vue'
import EmployeeSelect from '@/components/employee/elements/Select.vue'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  OCustomFields,
} from '@/components/elements'

import {
  DateField,
} from '@/components/fields'

import { Order } from '~/_api/models/order'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:order': [value: any]
}>()

const { $api, $notification, $auth, $i18n } = useNuxtApp()

const dialog = ref<any>(null)
const form = ref<any>(null)

const stagingOrder = ref<any>(null)

const timezone = computed(() => {
  return $auth?.info?.location?.timezoneUnix
})

function open() {
  stagingOrder.value = new Order({ ...props.order })

  dialog.value.open()
}

async function confirm() {
  emit('update:loading', true)

  try {
    const response = await $api.order.save(stagingOrder.value)
    const order = response.data

    emit('update:order', order)
    emit('update:loading', false)

    $notification.toast({
      title: $i18n.t('components.order.actions.editOrder.success'),
      text: $i18n.t('components.order.actions.editOrder.successInfo'),
      variant: 'success',
    })

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate() {
  return form.value.validate()
}

defineExpose({ open })
</script>
